import { useState } from 'react';
import { css } from '@emotion/react';
import { Link, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import type { Site } from '@hotjar/site';
import styled from '@emotion/styled';
import { SiteSelector } from './SiteSelector';

const getUrlHash = (): string => {
  return window.location.hash;
};

const S = {
  Subtitle: styled.h5`
    font-size: 16px;
    letter-spacing: 0.15px;
    color: ${tokens.color.text_default};
    font-weight: 600;
    margin: 0;
  `,
  Description: styled.p`
    font-size: 14px;
    color: ${tokens.color.text_default};
  `,
  SiteSelectTitle: styled.p`
    font-size: 16px;
    color: ${tokens.color.text_default};
    margin-top: 20px;
  `,
};

interface ContentHeaderProps {
  selectedSite: Site;
  sites: Array<Site>;
  changeSite: (siteId: number, urlHash: string) => void;
}

export const ContentHeader = ({
  selectedSite,
  sites,
  changeSite,
}: ContentHeaderProps): JSX.Element => {
  const { t } = useTranslation();
  const { click } = useTrack();
  const [showSiteSelector, setShowSiteSelector] = useState(false);

  const trackClick = (elementName: string, newSiteId?: number) => {
    if (!selectedSite) {
      return;
    }
    click(elementName, 'site.settings', {
      account_id: selectedSite.account.id,
      organization_id: selectedSite.organization.id,
      site_id: newSiteId || selectedSite.id,
      state_event_was_triggered_on: 'site.settings',
      url_event_was_triggered_on: `/site/${selectedSite.id}/settings`,
    });
  };

  const handleSiteChange = (event: React.FormEvent<HTMLSelectElement>) => {
    if (event.currentTarget.value) {
      const newSelectedSiteId = parseInt(event.currentTarget.value, 10);
      trackClick('site.settings-changed-site', newSelectedSiteId);
      changeSite(newSelectedSiteId, getUrlHash().substring(1));
    }
  };

  return (
    <div
      css={css`
        margin-bottom: 24px;
      `}
    >
      <S.Subtitle>{t('common:site-settings.subtitle', { siteUrl: selectedSite.name })}</S.Subtitle>
      <S.Description>
        {t('common:site-settings.description')}{' '}
        {sites.length > 1 && !showSiteSelector && (
          <>
            <Link
              kind="inline"
              as="button"
              onClick={() => {
                trackClick('site.settings-opened-site-selector');
                setShowSiteSelector(true);
              }}
            >
              {t('common:site-settings.change-site')}
            </Link>
            .
          </>
        )}
      </S.Description>

      {showSiteSelector && (
        <>
          <S.SiteSelectTitle>{t('common:site-settings.select-a-site')}</S.SiteSelectTitle>
          <SiteSelector onChange={handleSiteChange} selectedId={selectedSite.id} sites={sites} />
        </>
      )}
    </div>
  );
};
